// frontend/src/pages/admin/SystemSettlements.jsx
import { useState, useEffect } from 'react';
import { settlementAPI } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';

const SystemSettlements = () => {
  // State - ЗААВАЛ хоосон array/object-ээр эхлүүлэх
  const [companies, setCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [dateRange, setDateRange] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [filterInfo, setFilterInfo] = useState(null);
  
  // Modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    payment_reference: '',
    notes: ''
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN').format(amount || 0) + '₮';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('mn-MN');
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'today': return 'Өнөөдөр';
      case 'yesterday': return 'Өчигдөр';
      case 'week': return 'Сүүлийн 7 хоног';
      case 'month': return 'Сүүлийн 30 хоног';
      case 'custom': return 'Сонгосон хугацаа';
      default: return 'Сүүлийн 30 хоног';
    }
  };

  // Fetch data
  const fetchCompanySettlements = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { date_range: dateRange };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      if (selectedCompany !== 'all') {
        params.company_id = selectedCompany;
      }
      
      const response = await settlementAPI.getAllCompanySettlements(params);
      
      // Response шалгах
      if (response && response.data && response.data.success && response.data.data) {
        const data = response.data.data;
        const settlements = Array.isArray(data.settlements) ? data.settlements : [];
        
        setCompanies(settlements);
        setSummary(data.summary || {});
        setFilterInfo(data.filter || null);
        
        // Store all companies for filter dropdown
        if (allCompanies.length === 0 && settlements.length > 0) {
          const companyList = settlements
            .filter(s => s && s.company && s.company.id)
            .map(s => ({
              id: s.company.id,
              name: s.company.name || 'Unknown'
            }));
          setAllCompanies(companyList);
        }
      } else {
        // Response буруу
        setCompanies([]);
        setSummary({});
        setFilterInfo(null);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setCompanies([]);
      setSummary({});
      setFilterInfo(null);
      
      // 403 эрхийн алдаа
      if (err?.response?.status === 403) {
        setError('Танд энэ хэсэгт хандах эрх байхгүй байна');
      } else {
        setError('Мэдээлэл татахад алдаа гарлаа');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanySettlements();
  }, []);

  // Calculate totals for cards
  const calculateTotals = () => {
    const defaultTotals = {
      totalDeliveries: 0,
      successfulDeliveries: 0,
      periodCommission: 0,
      totalBalance: 0
    };

    if (!Array.isArray(companies) || companies.length === 0) {
      return defaultTotals;
    }

    try {
      return companies.reduce((acc, item) => {
        if (!item) return acc;
        return {
          totalDeliveries: acc.totalDeliveries + (item.period?.total_deliveries || item.today?.delivered_count || 0),
          successfulDeliveries: acc.successfulDeliveries + (item.period?.delivered_count || item.today?.delivered_count || 0),
          periodCommission: acc.periodCommission + (item.period?.commission_amount || item.today?.commission_amount || 0),
          totalBalance: acc.totalBalance + (item.balance?.balance || 0)
        };
      }, defaultTotals);
    } catch (e) {
      console.error('Calculate totals error:', e);
      return defaultTotals;
    }
  };

  const totals = calculateTotals();

  // View detail
  const handleViewDetail = async (company) => {
    if (!company || !company.company || !company.company.id) {
      console.error('Company data missing');
      return;
    }
    
    setSelectedCompanyData(company);
    setShowDetailModal(true);
    setDetailLoading(true);
    setCompanyDetail(null);
    
    try {
      const params = { date_range: dateRange };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      
      const response = await settlementAPI.getCompanySettlementDetail(company.company.id, params);
      if (response && response.data && response.data.success) {
        setCompanyDetail(response.data.data || null);
      }
    } catch (err) {
      console.error('Detail fetch error:', err);
      setCompanyDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open payment modal
  const handleOpenPayment = (company) => {
    if (!company || !company.balance) {
      console.error('Company balance data missing');
      return;
    }
    
    setSelectedCompanyData(company);
    setPaymentForm({
      amount: String(company.balance.balance || 0),
      payment_method: 'bank_transfer',
      payment_reference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  // Record payment
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      alert('Төлбөрийн дүн оруулна уу');
      return;
    }

    if (!selectedCompanyData || !selectedCompanyData.company) {
      alert('Компани сонгогдоогүй байна');
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await settlementAPI.recordCompanyPayment(selectedCompanyData.company.id, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference,
        notes: paymentForm.notes
      });

      if (response && response.data && response.data.success) {
        alert('Төлбөр амжилттай бүртгэгдлээ');
        setShowPaymentModal(false);
        fetchCompanySettlements();
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Төлбөр бүртгэхэд алдаа гарлаа');
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatPercent = (success, total) => {
    if (!total || total === 0) return '0%';
    return Math.round((success / total) * 100) + '%';
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Уншиж байна...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-6xl mb-4">🚫</div>
            <p className="text-red-600 font-medium">{error}</p>
            <button 
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              ← Буцах
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Төлбөр тооцоо</h1>
          <p className="text-gray-500 mt-1">Компаниудын системийн шимтгэл тооцоо</p>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Company Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Компани</label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Бүгд ({allCompanies.length})</option>
                {allCompanies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="today">Өнөөдөр</option>
                <option value="yesterday">Өчигдөр</option>
                <option value="week">Сүүлийн 7 хоног</option>
                <option value="month">Сүүлийн 30 хоног</option>
                <option value="custom">Хугацаа сонгох</option>
              </select>
            </div>

            {dateRange === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх огноо</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах огноо</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </>
            )}

            <div className={`flex items-end ${dateRange === 'custom' ? 'lg:col-span-5' : ''}`}>
              <button
                onClick={fetchCompanySettlements}
                disabled={loading}
                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
                <span>Хайх</span>
              </button>
            </div>
          </div>

          {/* Active Filter Info */}
          {filterInfo && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md">
                  <span>📅</span>
                  <span>{getDateRangeLabel()}:</span>
                  <span className="font-medium">
                    {filterInfo.start_date === filterInfo.end_date 
                      ? formatDate(filterInfo.start_date)
                      : `${formatDate(filterInfo.start_date)} - ${formatDate(filterInfo.end_date)}`
                    }
                  </span>
                </span>
                {selectedCompany !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-md">
                    <span>🏢</span>
                    <span className="font-medium">
                      {allCompanies.find(c => String(c.id) === String(selectedCompany))?.name || ''}
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Summary Cards - 4 Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Нийт хүргэлт */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Нийт хүргэлт</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <p className="text-3xl font-bold">{totals.successfulDeliveries}</p>
                  <span className="text-blue-200 text-lg">/{totals.totalDeliveries || totals.successfulDeliveries}</span>
                </div>
                <p className="text-blue-100 text-xs mt-2">
                  {formatPercent(totals.successfulDeliveries, totals.totalDeliveries || totals.successfulDeliveries)} амжилттай
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">🚚</span>
              </div>
            </div>
          </div>

          {/* Card 2: Нийт компани */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Нийт компани</p>
                <p className="text-3xl font-bold mt-2">{companies.length}</p>
                <p className="text-purple-100 text-xs mt-2">Идэвхтэй</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">🏢</span>
              </div>
            </div>
          </div>

          {/* Card 3: Нийт шимтгэл */}
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-indigo-100 text-sm font-medium">Нийт шимтгэл</p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(totals.periodCommission)}</p>
                <p className="text-indigo-100 text-xs mt-2">{getDateRangeLabel()}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">💰</span>
              </div>
            </div>
          </div>

          {/* Card 4: Авах үлдэгдэл */}
          <div className={`bg-gradient-to-br rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all ${
            totals.totalBalance > 0 
              ? 'from-orange-500 to-orange-600' 
              : 'from-emerald-500 to-emerald-600'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm font-medium ${totals.totalBalance > 0 ? 'text-orange-100' : 'text-emerald-100'}`}>
                  Авах үлдэгдэл
                </p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(totals.totalBalance)}</p>
                <p className={`text-xs mt-2 ${totals.totalBalance > 0 ? 'text-orange-100' : 'text-emerald-100'}`}>
                  {totals.totalBalance > 0 ? 'Төлөгдөөгүй' : 'Тооцоо дууссан'}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">{totals.totalBalance > 0 ? '⚠️' : '✅'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Компаниудын жагсаалт</h3>
            <span className="text-sm text-gray-500">{companies.length} компани</span>
          </div>
          
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Компани</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Нэгж шимтгэл</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Хүргэлт</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Шимтгэл</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Үлдэгдэл</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Үйлдэл</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-4xl">📭</span>
                        <span>Мэдээлэл байхгүй байна</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  companies.map((item, index) => {
                    if (!item || !item.company) return null;
                    
                    return (
                      <tr key={item.company.id || index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                              <span className="text-lg text-white font-bold">
                                {item.company.name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{item.company.name || '-'}</div>
                              <div className="text-sm text-gray-500">{item.company.phone || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(item.company.commission_value)}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-semibold text-blue-600">
                            {item.period?.delivered_count || item.today?.delivered_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-medium text-gray-900">
                            {formatCurrency(item.period?.commission_amount || item.today?.commission_amount || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`font-semibold ${(item.balance?.balance || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {formatCurrency(item.balance?.balance)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleViewDetail(item)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Дэлгэрэнгүй"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {(item.balance?.balance || 0) > 0 && (
                              <button
                                onClick={() => handleOpenPayment(item)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Төлбөр бүртгэх"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedCompanyData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-500 to-indigo-500">
                <div className="text-white">
                  <h2 className="text-xl font-bold">{selectedCompanyData.company?.name || '-'}</h2>
                  <p className="text-blue-100 text-sm">
                    {filterInfo && `${formatDate(filterInfo.start_date)} - ${formatDate(filterInfo.end_date)}`}
                  </p>
                </div>
                <button 
                  onClick={() => setShowDetailModal(false)} 
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(95vh-80px)]">
                {detailLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : companyDetail ? (
                  <div className="space-y-6">
                    {/* Balance Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 text-center border border-gray-200">
                        <p className="text-sm text-gray-500">Нийт шимтгэл</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(companyDetail.balance?.total_commission)}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-200">
                        <p className="text-sm text-green-600">Төлсөн дүн</p>
                        <p className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(companyDetail.balance?.total_paid)}</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 text-center border border-orange-200">
                        <p className="text-sm text-orange-600">Үлдэгдэл</p>
                        <p className="text-2xl font-bold text-orange-700 mt-1">{formatCurrency(companyDetail.balance?.balance)}</p>
                      </div>
                    </div>

                    {/* Daily Stats */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span>📅</span> Өдөр бүрийн хүргэлт
                      </h3>
                      <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
                          <table className="min-w-full">
                            <thead className="sticky top-0 bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Огноо</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Нийт</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Хүргэсэн</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Шимтгэл</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {Array.isArray(companyDetail.daily_stats) && companyDetail.daily_stats.length > 0 ? (
                                companyDetail.daily_stats.map((day, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{formatDate(day.delivery_date)}</td>
                                    <td className="px-4 py-3 text-sm text-center text-gray-600">{day.total || 0}</td>
                                    <td className="px-4 py-3 text-sm text-center text-green-600 font-medium">{day.delivered || 0}</td>
                                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(day.commission)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500 text-sm">
                                    Хүргэлтийн түүх байхгүй
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Transaction History */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span>💳</span> Төлбөрийн түүх
                      </h3>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {Array.isArray(companyDetail.transactions) && companyDetail.transactions.length > 0 ? (
                          companyDetail.transactions.map((tx) => (
                            <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  tx.transaction_type === 'payment' ? 'bg-green-100' : 'bg-orange-100'
                                }`}>
                                  {tx.transaction_type === 'payment' ? (
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {tx.transaction_type === 'payment' ? 'Төлбөр хүлээн авсан' : 'Системийн шимтгэл'}
                                  </p>
                                  <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                                </div>
                              </div>
                              <span className={`font-semibold text-sm ${tx.amount < 0 ? 'text-green-600' : 'text-orange-600'}`}>
                                {tx.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(tx.amount))}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-gray-500 py-4 text-sm">Төлбөрийн түүх байхгүй</p>
                        )}
                      </div>
                    </div>

                    {/* Payment Button */}
                    {(companyDetail.balance?.balance || 0) > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setShowDetailModal(false);
                            handleOpenPayment(selectedCompanyData);
                          }}
                          className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 transition-all flex items-center justify-center gap-2 shadow-lg"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span>Төлбөр бүртгэх</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-12">Мэдээлэл олдсонгүй</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedCompanyData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-500 rounded-t-xl">
                <h2 className="text-xl font-bold text-white">Төлбөр бүртгэх</h2>
                <p className="text-green-100 text-sm">{selectedCompanyData.company?.name || '-'}</p>
              </div>
              
              <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 text-center border border-orange-200">
                  <p className="text-sm text-orange-600">Авах үлдэгдэл</p>
                  <p className="text-3xl font-bold text-orange-700 mt-1">{formatCurrency(selectedCompanyData.balance?.balance)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Төлбөрийн дүн *</label>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Төлбөрийн хэлбэр</label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="bank_transfer">🏦 Банкны шилжүүлэг</option>
                    <option value="cash">💵 Бэлэн мөнгө</option>
                    <option value="qpay">📱 QPay</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Гүйлгээний дугаар</label>
                  <input
                    type="text"
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Заавал биш"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Тэмдэглэл</label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    rows={2}
                    placeholder="Нэмэлт тэмдэглэл..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Болих
                  </button>
                  <button
                    type="submit"
                    disabled={paymentLoading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg"
                  >
                    {paymentLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : null}
                    <span>{paymentLoading ? 'Хадгалж байна...' : 'Бүртгэх'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SystemSettlements;